import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateShoreUserTitleRequest,
    ShoreUserTitle,
    ShoreUserTitleRequest,
    ShoreUserTitleResponse,
    UpdateShoreUserTitleRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-user-title.api.ts';
import {
    createApiShoreUserTitle,
    deleteApiShoreUserTitle,
    getApiShoreUserTitle,
    updateApiShoreUserTitle,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-user-title.api.ts';
import { extractError } from '@utils/slice.ts';

export interface ShoreUserTitleError {
    message?: string;
    [k: string]: any;
}

export interface ShoreUserTitleState {
    error: ShoreUserTitleError | null;

    commonShoreUserTitle: ShoreUserTitleResponse | null;
    shoreUserTitles: ShoreUserTitle[];
    pagination: CommonTablePaginatedResponse;

    updatedShoreUserTitle: ShoreUserTitle | null;
    currentGetRequestId: string | null;

    isGetShoreUserTitlePending: boolean;
    isCreateShoreUserTitlePending: boolean;
    isUpdateShoreUserTitlePending: boolean;
    isDeleteShoreUserTitlePending: boolean;
}

const initialState: ShoreUserTitleState = {
    error: null,

    commonShoreUserTitle: null,
    shoreUserTitles: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedShoreUserTitle: null,
    currentGetRequestId: null,

    isGetShoreUserTitlePending: false,
    isCreateShoreUserTitlePending: false,
    isUpdateShoreUserTitlePending: false,
    isDeleteShoreUserTitlePending: false,
};

export const getShoreUserTitle = createAsyncThunk<
    ShoreUserTitleResponse,
    { clientId: number; tenantAlias: string; payload: ShoreUserTitleRequest },
    { rejectValue: ShoreUserTitleError }
>('adminCenterShoreUserTitle/getShoreUserTitle', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiShoreUserTitle(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createShoreUserTitle = createAsyncThunk<
    ShoreUserTitle,
    { clientId: number; tenantAlias: string; payload: CreateShoreUserTitleRequest },
    { rejectValue: ShoreUserTitleError }
>('adminCenterShoreUserTitle/createShoreUserTitle', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiShoreUserTitle(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreUserTitle = createAsyncThunk<
    ShoreUserTitle,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateShoreUserTitleRequest },
    { rejectValue: ShoreUserTitleError }
>('adminCenterShoreUserTitle/updateShoreUserTitle', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiShoreUserTitle(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreUserTitle = createAsyncThunk<
    ShoreUserTitle,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: ShoreUserTitleError }
>('adminCenterShoreUserTitle/deleteShoreUserTitle', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiShoreUserTitle(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const shoreUserTitleSlice = createSlice({
    name: 'adminCenterShoreUserTitle',
    initialState,
    reducers: {
        resetShoreUserTitleState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getShoreUserTitle.pending, (state, action) => {
            state.isGetShoreUserTitlePending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getShoreUserTitle.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreUserTitlePending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonShoreUserTitle = action.payload;
            state.shoreUserTitles = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getShoreUserTitle.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreUserTitlePending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createShoreUserTitle.pending, (state) => {
            state.isCreateShoreUserTitlePending = true;
            state.error = null;
        });
        builder.addCase(createShoreUserTitle.fulfilled, (state, action) => {
            state.isCreateShoreUserTitlePending = false;
            state.updatedShoreUserTitle = action.payload;
        });
        builder.addCase(createShoreUserTitle.rejected, (state, action) => {
            state.isCreateShoreUserTitlePending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateShoreUserTitle.pending, (state) => {
            state.isUpdateShoreUserTitlePending = true;
            state.error = null;
        });
        builder.addCase(updateShoreUserTitle.fulfilled, (state, action) => {
            state.isUpdateShoreUserTitlePending = false;
            state.updatedShoreUserTitle = action.payload;
        });
        builder.addCase(updateShoreUserTitle.rejected, (state, action) => {
            state.isUpdateShoreUserTitlePending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteShoreUserTitle.pending, (state) => {
            state.isDeleteShoreUserTitlePending = true;
            state.error = null;
        });
        builder.addCase(deleteShoreUserTitle.fulfilled, (state, action) => {
            state.isDeleteShoreUserTitlePending = false;
            state.updatedShoreUserTitle = action.payload;
            state.shoreUserTitles = state.shoreUserTitles.filter(
                (shoreUserTitle) => shoreUserTitle.id !== action.payload.id,
            );
        });
        builder.addCase(deleteShoreUserTitle.rejected, (state, action) => {
            state.isDeleteShoreUserTitlePending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { shoreUserTitleResponse } = action.payload;

            if (!shoreUserTitleResponse) return;

            const { data, pagination } = shoreUserTitleResponse;
            state.commonShoreUserTitle = shoreUserTitleResponse;
            state.shoreUserTitles = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetShoreUserTitleState } = shoreUserTitleSlice.actions;
export default shoreUserTitleSlice.reducer;
